import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import reportResources from './reportResources';
import ReportHeading from '../ReportHeading';
import ReportResources from '../Resources';
import GrowthReportCard from './GrowthReportCard';
import GrowthReportFilters from './GrowthReportFilters';
import { getGrowthReportsHeaderText } from '../GrowthReports/reportDetails';
import DropdownMenu from '../DropdownMenu';
import QuickSearch from '../QuickSearch';
import BrowserContext from '../BrowserContext';
import accordionUtils from '../Accordion/utils';

import Accordions from '../Accordion/Accordions';
import tabDetails from '../Navigation/tabDetails';
import styles from './GrowthReports.module.css';
import dropdownStyles from '../DropdownMenu/DropdownMenu.module.css';
import mainStyles from '../MainContainer.module.css';
import { sortingMethods } from './reportDetails';
import { browserRemoveTransition } from '../utils';
import { filterReportsFromFlags, filterLaunchDarklyVersionedReports } from 'utils/LaunchDarkly';

import commonStrings from '../../resources/commonStrings';

const reportShape = PropTypes.shape({ identifier: PropTypes.string.isRequired });

const propTypes = {
  accordionDetails: PropTypes.instanceOf(Map).isRequired,
  allReportDetails: PropTypes.arrayOf(reportShape).isRequired,
  filteredGrowthReportDetails: PropTypes.arrayOf(reportShape).isRequired,
  onAccordionStateChange: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onSortSelection: PropTypes.func.isRequired,
  sortSelection: PropTypes.shape({
    isExpanded: PropTypes.bool.isRequired,
    value: PropTypes.string.isRequired,
    previousValue: PropTypes.string.isRequired,
  }).isRequired,
  sortExpandStateFn: PropTypes.func.isRequired,
  sessionToken: PropTypes.shape().isRequired,
  launchDarklyFlags: PropTypes.instanceOf(Map),
};

const DropdownMenuWithForwardRef = React.forwardRef((props, ref) => (
  <DropdownMenu {...props} sortingDropdownRef={ref} />
  // sortingDropdownRef={ref} - This ref is not passed as DropdownMenu's actual ref because its purpose
  //     is to be assigned to the span which opens the dropdown menu inside of DropdownMenu
));

function GrowthReports({
  accordionDetails,
  allReportDetails,
  filteredGrowthReportDetails,
  onAccordionStateChange,
  onResetFilters,
  onSortSelection,
  sortSelection,
  sortExpandStateFn,
  sessionToken,
  flags: launchDarklyFlags, // Rename LaunchDarkly's provided prop name for clarity
}) {
  const launchDarklyFilteredReports = filterReportsFromFlags(filteredGrowthReportDetails, launchDarklyFlags);
  const filteredReports = filterLaunchDarklyVersionedReports(launchDarklyFilteredReports, launchDarklyFlags);

  const browserName = useContext(BrowserContext);

  const sortMenuOptions = [
    { displayText: commonStrings.growthReportsSorting.recommended, value: sortingMethods.recommended },
    { displayText: commonStrings.growthReportsSorting.asc, value: sortingMethods.asc },
    { displayText: commonStrings.growthReportsSorting.desc, value: sortingMethods.desc },
  ];

  const filterAccordionDetails = new Map();
  const widgetAccordionDetails = new Map();

  accordionDetails.forEach((value, key) => {
    if (accordionUtils.filterIdentifiers.includes(key)) {
      filterAccordionDetails.set(key, value);
    } else {
      widgetAccordionDetails.set(key, value);
    }
  });

  const reportCards =
    filteredReports.length !== 0
      ? filteredReports.map((reportDetail, index) => {
          let cardDetails = reportDetail;

          // Swap Spanish text if flag is on for batchServiceFamilyReport
          if (
            reportDetail.identifier === 'batchServiceFamilyReport' &&
            launchDarklyFlags?.insights1483FamilyReportSpanish
          ) {
            cardDetails = {
              ...reportDetail,
              details: {
                ...reportDetail.details,
                listItems: commonStrings.reportDetailsList.batchServiceFamilyReportSpanish,
              },
            };
          }

          const transitionOptions = browserRemoveTransition(
            {
              classNames: styles,
              unmountOnExit: true,
              key: reportDetail.identifier,
              timeout: styles.transitionTime * 1000,
            },
            browserName
          );

          return (
            <CSSTransition {...transitionOptions}>
              <GrowthReportCard key={cardDetails.identifier} cardDetails={cardDetails} index={index} />
            </CSSTransition>
          );
        })
      : null;

  const headingText =
    filteredReports.length !== 0
      ? getGrowthReportsHeaderText(
          accordionDetails.get(accordionUtils.REPORT_LEVEL_FILTER).itemState.roleLevel,
          accordionDetails.get(accordionUtils.REPORT_LEVEL_FILTER).itemState.value,
          accordionDetails.get(accordionUtils.WHAT_IM_DOING_FILTER).itemState.value
        )
      : commonStrings.growth.noReportCards;

  const asideClasses = `${styles['column']} ${styles['width-3']}`;
  const sectionClasses = `${styles['column']} ${styles['width-9']}`;
  const headingClasses = `${dropdownStyles['container']} ${styles['sort-menu']}`;

  let sortingDropdown = React.createRef();

  const handleKeystrokeReportsSkipLink = (event) => {
    if (event.key.toLowerCase().indexOf('enter') === 0) {
      sortingDropdown.current.focus();
    }
  };

  const handleClickReportsSkipLink = (event) => {
    sortingDropdown.current.focus();
  };

  return (
    <React.Fragment>
      <h1 className={mainStyles['page-title']}>{commonStrings.growth.pageTitle}</h1>
      <ReportHeading />
      <aside className={asideClasses}>
        {filterAccordionDetails.size > 0 ? (
          <GrowthReportFilters
            onAccordionStateChange={onAccordionStateChange}
            allReportDetails={allReportDetails}
            filterAccordionDetails={filterAccordionDetails}
            onResetFilters={onResetFilters}
            sessionToken={sessionToken}
            sortingMethod={sortSelection.value}
          />
        ) : null}
        <a
          className={styles['skip-link']}
          onKeyUp={handleKeystrokeReportsSkipLink}
          onClick={handleClickReportsSkipLink}
          tabIndex={0}
        >
          {commonStrings.skipLinks.skipToReports}
        </a>
        <QuickSearch />
        <Accordions
          accordions={widgetAccordionDetails}
          containingTab={tabDetails.growthReports.urlEndPoint}
          onAccordionStateChange={onAccordionStateChange}
        />
        <ReportResources resources={reportResources} />
      </aside>
      <section role="tabpanel" className={sectionClasses}>
        <div className={headingClasses}>
          <h2 className={dropdownStyles['label']} aria-live="polite">
            {headingText}
          </h2>
          <span>
            <span id={`growthReportSorting-title`} className={dropdownStyles['label']}>
              {commonStrings.growthReportsSorting.title}
            </span>
            <DropdownMenuWithForwardRef
              containingTab={tabDetails.growthReports.urlEndPoint}
              identifier={'growthReportSorting'}
              selectionItems={sortMenuOptions}
              isExpanded={sortSelection.isExpanded}
              title={commonStrings.growthReportsSorting.title}
              ariaLabel={commonStrings.sortByAriaLabel(sortSelection.value)}
              value={sortSelection.value}
              previousValue={sortSelection.previousValue}
              onSortSelection={onSortSelection}
              sortExpandStateFn={sortExpandStateFn}
              classes={styles['sort-menu']}
              ref={sortingDropdown}
            />
          </span>
        </div>
        <div className={styles['report-cards']}>
          <TransitionGroup component={null}>{reportCards}</TransitionGroup>
        </div>
        <footer className={mainStyles['confidentiality-statement']}>{commonStrings.pageFooter}</footer>
      </section>
    </React.Fragment>
  );
}

GrowthReports.propTypes = propTypes;

export default GrowthReports;
